import { useQueryClient, QueryClient } from '@tanstack/react-query';

import {
  brainsEndpoints,
  businessHoursEndpoints,
  campaignsEndpoints,
  collectionsEndpoints,
  datasourceEndpoints,
  desksEndpoints,
  dialogsEndpoints,
  entitiesEndpoints,
  integrationsEndpoints,
  intentsEndpoints,
  macrosEndpoints,
  rulesEndpoints,
  versionsEndpoints,
  webhooksEndpoints,
  bundlesEndpoints,
  viewsEndpoints,
} from '@/api/endpoints';
import { brainCache, API as brainAPI } from '@/hooks/useBrains';
import { API as businessHoursAPI, businessHourCache } from '@/hooks/useBusinessHours';
import { collectionCache, API as collectionAPI } from '@/hooks/useCollections';
import { datasourceCache, API as datasourceAPI } from '@/hooks/useDatasources';
import { deskCache, API as deskAPI } from '@/hooks/useDesks';
import { API as dialogAPI, dialogCache } from '@/hooks/useDialogs';
import { API as entityAPI, entityCache } from '@/hooks/useEntities';
import { API as integrationAPI, integrationCache } from '@/hooks/useIntegrations';
import { API as intentAPI, intentCache } from '@/hooks/useIntents';
import { API as macroAPI, macroCache } from '@/hooks/useMacros';
import { API as versionAPI, onVersionCreated, onVersionRemoved } from '@/hooks/useVersions';
import { webhookCache, API as webhookAPI } from '@/hooks/useWebhooks';
import { EntityCache } from '@/hooks/utils/entityCache';
import { API as bundlesAPI, bundleCache } from '@/modules/bundles/hooks/useBundles';
import {
  onCampaignRemoved,
  onCampaignCreated,
  onCampaignUpdated,
  API as campaignAPI,
} from '@/modules/campaigns/api';
import {
  API as ruleAPI,
  onRuleCreated,
  onRuleUpdated,
  onRuleRemoved,
} from '@/modules/rules/hooks/useRules';
import { Module } from '@/shared/types/permissions';
import { Rule } from '@/shared/types/rules';
import { View } from '@/shared/types/views';

import { API as viewsAPI, onViewUpdated, onViewCreated, onViewRemoved } from '../useViews';

type CacheHandlers = {
  create: (queryClient: QueryClient, payload: unknown, payload2?: unknown) => void;
  update?: (queryClient: QueryClient, payload: unknown) => void;
  remove: (queryClient: QueryClient, parentId: string, id: string | number) => void;
  rename?: (queryClient: QueryClient, name: string, payload: unknown) => void;
};

type handlerObj = {
  [key in Module]?: CacheHandlers & {
    API: (parentId: string, id?: string | number) => Promise<unknown>;
    endpoints: (parentId: string, id?: string | number) => string;
  };
};

/**
 * Adapts an EntityCache to the RTM handler shape. Root resources (brains, desks, collections)
 * are removed by their id alone, which the RTM hooks pass as `parentId`.
 */
const fromEntityCache = <TEntity>(cache: EntityCache<TEntity>): CacheHandlers => ({
  create: (queryClient, entity) => cache.created(queryClient, entity as TEntity),
  update: (queryClient, entity) => cache.updated(queryClient, entity as TEntity),
  remove: (queryClient, parentId, id) =>
    cache.removed(queryClient, id === undefined ? { id: parentId } : { parentId, id: String(id) }),
  rename: (queryClient, previousId, entity) =>
    cache.updated(queryClient, entity as TEntity, { previousId }),
});

const handler: handlerObj = {
  brains: {
    API: brainAPI.getBrain,
    endpoints: brainsEndpoints.brain,
    ...fromEntityCache(brainCache),
  },
  campaigns: {
    API: campaignAPI.getCampaign,
    endpoints: campaignsEndpoints.campaign,
    create: onCampaignCreated,
    update: onCampaignUpdated,
    remove: onCampaignRemoved,
  },
  collections: {
    API: collectionAPI.getCollection,
    endpoints: collectionsEndpoints.collection,
    ...fromEntityCache(collectionCache),
  },
  datasources: {
    API: datasourceAPI.getDatasource,
    endpoints: datasourceEndpoints.datasource,
    ...fromEntityCache(datasourceCache),
  },
  desks: { API: deskAPI.getDesk, endpoints: desksEndpoints.desk, ...fromEntityCache(deskCache) },
  integrations: {
    API: integrationAPI.getIntegration,
    endpoints: integrationsEndpoints.integration,
    ...fromEntityCache(integrationCache),
  },
  entities: {
    API: entityAPI.getEntity,
    endpoints: entitiesEndpoints.entity,
    ...fromEntityCache(entityCache),
  },
  intents: {
    API: intentAPI.getIntent,
    endpoints: intentsEndpoints.intent,
    ...fromEntityCache(intentCache),
  },
  dialogs: {
    API: dialogAPI.getDialog,
    endpoints: dialogsEndpoints.dialog,
    ...fromEntityCache(dialogCache),
  },
  webhooks: {
    API: webhookAPI.getWebhook,
    endpoints: webhooksEndpoints.webhook,
    ...fromEntityCache(webhookCache),
  },
  macros: {
    API: macroAPI.getMacro,
    endpoints: macrosEndpoints.macro,
    ...fromEntityCache(macroCache),
  },
  versions: {
    API: (id, v: number) => versionAPI.getVersion(id, v),
    endpoints: (id, v: number) => versionsEndpoints.version(id, v),
    create: onVersionCreated,
    remove: (queryClient: QueryClient, id: string, v: number) =>
      onVersionRemoved(queryClient, id, v),
  },
  rules: {
    API: ruleAPI.getRule,
    endpoints: rulesEndpoints.rule,
    create: (queryClient: QueryClient, newRule: Rule) =>
      onRuleCreated(queryClient, newRule, newRule.desk_id),
    update: (queryClient: QueryClient, newRule: Rule) =>
      onRuleUpdated(queryClient, newRule, newRule.desk_id),
    remove: onRuleRemoved,
  },
  business_hours: {
    API: businessHoursAPI.getBusinessHour,
    endpoints: businessHoursEndpoints.businessHour,
    ...fromEntityCache(businessHourCache),
  },
  bundles: {
    API: bundlesAPI.getBundle,
    endpoints: bundlesEndpoints.bundle,
    ...fromEntityCache(bundleCache),
  },
  views: {
    API: viewsAPI.getView,
    endpoints: viewsEndpoints.view,
    create: (queryClient: QueryClient, newView: View) => onViewCreated(queryClient, newView),
    update: (queryClient: QueryClient, newView: View) => onViewUpdated(queryClient, newView),
    remove: onViewRemoved,
  },
};

/** Resource ids are strings, except brain versions, which are numbers. */
type RtmId = string | number;

export type RtmActions = ReturnType<typeof useRtm>;

export const useRtm = () => {
  const queryClient = useQueryClient();

  const create = async (type: Module, parentId: string, id?: RtmId) => {
    try {
      const newData = await queryClient.fetchQuery({
        queryKey: [handler[type].endpoints(parentId, id)],
        queryFn: () => handler[type].API(parentId, id),
      });
      if (type === 'versions') {
        handler[type].create(queryClient, parentId, newData);
      } else {
        handler[type].create(queryClient, newData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const update = async (type: Module, parentId: string, id?: RtmId) => {
    try {
      const newData = await queryClient.fetchQuery({
        queryKey: [handler[type].endpoints(parentId, id)],
        queryFn: () => handler[type].API(parentId, id),
        staleTime: 0,
      });
      handler[type].update(queryClient, newData);
    } catch (error) {
      console.error(error);
    }
  };

  const remove = async (type: Module, parentId: string, id?: RtmId) => {
    try {
      handler[type].remove(queryClient, parentId, id);
    } catch (error) {
      console.error(error);
    }
  };

  const rename = async (type: Module, parentId: string, oldName: string, newName: string) => {
    try {
      const newData = await queryClient.fetchQuery({
        queryKey: [handler[type].endpoints(parentId, newName)],
        queryFn: () => handler[type].API(parentId, newName),
        staleTime: 0,
      });

      handler[type].rename(queryClient, oldName, newData);
    } catch (error) {
      console.error(error);
    }
  };

  return { create, update, remove, rename };
};
